import { Component, OnInit, ElementRef, AfterViewInit, NgZone } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { PlayerService } from '../../services/player.service';
import { Player } from '../../objects/player';
import { LoginDialogComponent } from '../login-dialog/login-dialog.component';
import { CollectPhoneDialogComponent } from '../collect-phone-dialog/collect-phone-dialog.component';
import { SocialAuthService } from "@abacritt/angularx-social-login";
import { FacebookLoginProvider, GoogleLoginProvider } from "@abacritt/angularx-social-login";
import { SocialUser } from '@abacritt/angularx-social-login';
import { isValidEmail } from '../../shared/validators/email.validator';
import { isValidUsPhone, normalizeUsPhone } from '../../shared/validators/phone.validator';


@Component({
  selector: 's18-create-account',
  templateUrl: './create-account.component.html',
  styleUrls: ['./create-account.component.scss']
})
export class CreateAccountComponent implements OnInit, AfterViewInit {
  registerWithEmail: boolean = false;
  loginWithGoogle: boolean = false;
  loginWithFacebook: boolean = false;
  player: Player = new Player();
  sendingRequest: boolean = false;
  response = { response: '', new_player_id: '', error: '' };
  socialUser: SocialUser

  constructor(
    public dialogRef: MatDialogRef<CreateAccountComponent>,
    private dialog: MatDialog,
    private router: Router,
    private playerService: PlayerService,
    private snackBar: MatSnackBar,
    private element: ElementRef,
    private authService: SocialAuthService,
    private zone: NgZone
  ) {

  }


  signInWithGoogle(): void {
    // No-op: Google sign-in is now handled by the asl-google-signin-button directive
    // The authState subscription in ngOnInit handles the response
  }

  signInWithFB(): void {
    this.loginWithFacebook = true;
    this.authService.signIn(FacebookLoginProvider.PROVIDER_ID);
  }

  signOut(): void {
    this.authService.signOut();
  }

  ngOnInit() {
    // Subscribe to social auth state — fires when Google/Facebook sign-in completes
    this.authService.authState.subscribe((user) => {
      if (!user) return;
      this.socialUser = user;
      switch (user.provider) {
        case 'GOOGLE':
          this.loginWithGoogle = true;
          this.registerWithGoogle(user.idToken);
          break;
        case 'FACEBOOK':
          this.loginWithFacebook = true;
          // WI 58: Facebook does not reliably provide a phone, so collect one before creating
          // the account. Email is pre-filled from the Facebook profile.
          this.collectPhoneThen(user.email, (phone) => {
            this.playerService.registerWithEmail(user.email, user.firstName, user.lastName, "0", user.lastName, phone)
              .subscribe(
                resp => this.parseRegisterPlayerResponse(resp),
                error => {
                  this.snackBar.open("Error creating account", null, { duration: 3000 });
                  this.loginWithFacebook = false;
                }
              );
          }, () => { this.loginWithFacebook = false; });
          break;
      }
    });
  }
  forwardToLogin() {
    this.dialog.closeAll();
    this.router.navigateByUrl('/login')
  }
  ngAfterViewInit() {

  }

  registerWithGoogle(token: string) {
    this.playerService.registerWithGoogle(token).subscribe(
      resp => {
        // WI 58: the backend returns phone_required when the Google account has no phone.
        var parsed = this.tryParsePhoneRequired(resp);
        if (parsed) {
          this.loginWithGoogle = false;
          this.collectPhoneThen(parsed.email, (phone) => {
            this.loginWithGoogle = true;
            this.playerService.finalizeGoogleRegistration(token, phone).subscribe(
              finalResp => this.parseRegisterPlayerResponse(finalResp),
              error => {
                this.loginWithGoogle = false;
                this.snackBar.open("Error creating account", null, { panelClass: 'error-toast', duration: 3000, verticalPosition: 'top', horizontalPosition: 'right' });
              }
            );
          });
          return;
        }
        var ggkPlayer = JSON.parse(resp.d)[0];
        if (ggkPlayer) {
          this.playerService.setLoggedInPlayerInfo(ggkPlayer);
          this.playerService.loadAdminGroups(ggkPlayer.player_id.toString());
          this.dialogRef.close(ggkPlayer.player_id);
        }
      },
      error => {
        this.loginWithGoogle = false;
        this.snackBar.open("Error creating account", null, { panelClass: 'error-toast', duration: 3000, verticalPosition: 'top', horizontalPosition: 'right' });
      }
    );
  }

  closeDialog(dialogResponse) {
    this.dialogRef.close(dialogResponse);
  }

  // WI 58: the email/password form now requires a valid email AND a valid phone before submit.
  get canSubmit(): boolean {
    return isValidEmail(this.player.email) && isValidUsPhone(this.player.contact_phone);
  }

  registerUser() {
    if (!this.canSubmit) {
      this.response.error = "Enter a valid email and a valid 10-digit US phone number.";
      return;
    }
    this.sendingRequest = true;
    const phone = normalizeUsPhone(this.player.contact_phone);
    this.playerService.registerWithEmail(this.player.email, this.player.first, this.player.last, this.player.handicap ? this.player.handicap.toString() : '0', this.player.password, phone)
      .subscribe(
        resp => this.parseRegisterPlayerResponse(resp),
        error => {
          this.snackBar.open("Error creating account", null, { panelClass: 'error-toast', duration: 3000, verticalPosition: 'top', horizontalPosition: 'right' });
          this.sendingRequest = false;
        }
      );
  }

  parseRegisterPlayerResponse(resp) {
    this.sendingRequest = false;
    this.loginWithGoogle = false;
    this.loginWithFacebook = false;
    var ggkPlayer = JSON.parse(resp.d)[0];
    if (ggkPlayer) {
      // WI 75: signing up with an email that already exists LINKS to (and signs in to) the existing
      // account instead of creating a duplicate. Surface that so the user understands they were not
      // double-registered. (registerWithEmail returns the existing member login when the email matched.)
      this.snackBar.open(
        "You're signed in. If this email already had an account, we linked you to it instead of creating a new one.",
        null,
        { duration: 5000, verticalPosition: 'top', horizontalPosition: 'right' }
      );
      this.playerService.setLoggedInPlayerInfo(ggkPlayer);
      this.playerService.loadAdminGroups(ggkPlayer.player_id.toString());
      this.dialogRef.close(ggkPlayer.player_id);
    }
  }

  // WI 58: returns { email } when the backend response signals a phone is required, else null.
  private tryParsePhoneRequired(resp): { email: string } | null {
    try {
      const data = JSON.parse(resp.d);
      if (data && data.response === 'phone_required') {
        return { email: data.email };
      }
    } catch (e) {
      // response.d was an array (normal login) — not a phone_required signal
    }
    return null;
  }

  // WI 58: open the shared phone-collection dialog, pre-filling the email, then run onPhone(phone)
  // with a normalized 10-digit phone. onCancel (optional) runs if the user dismisses the dialog.
  private collectPhoneThen(email: string, onPhone: (phone: string) => void, onCancel?: () => void) {
    const ref = this.dialog.open(CollectPhoneDialogComponent, {
      disableClose: true,
      data: { email: email }
    });
    ref.afterClosed().subscribe((phone: string) => {
      if (phone) {
        onPhone(phone);
      } else if (onCancel) {
        onCancel();
      }
    });
  }
}
